import clsx from 'clsx'
import { type MouseEvent, useCallback } from 'react'
import type { TreeItem } from '@/types'
import { ArrowUpward, FilterList, FilterListOff } from '../../../primitives/index.js'
import { expandToSlug, useTreeApi } from '../../atoms/treeState.js'
import { useChangeFilter } from '../../hooks/useChangeFilter.js'
import { useStoryRoute } from '../../hooks/useStoryRoute.js'
import classes from './navigation-node-label.module.css'

export type NodeProps = {
  item: TreeItem
  isLeaf?: boolean
}

export function NavigationNodeLabel(props: NodeProps) {
  const {
    isLeaf,
    item: { title, slug },
  } = props

  const treeApi = useTreeApi()
  const { story, filter } = useStoryRoute()
  const changeFilter = useChangeFilter()

  const isActive = filter === slug
  const filterIcon = isActive ? <FilterListOff fontSize="small" /> : <FilterList fontSize="small" />

  const onClickFilter = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()

      if (isActive) {
        changeFilter('')
        treeApi?.focus(story)
      } else {
        changeFilter(slug)
        expandToSlug(slug)

        treeApi?.open(slug)
        treeApi?.focus(story)
      }
    },
    [story, changeFilter, isActive, slug, treeApi],
  )

  const onClickUp = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()

      const nextFilter = slug.split('/').slice(0, -1).join('/')
      changeFilter(nextFilter)
      expandToSlug(slug)

      treeApi?.open(nextFilter)
      treeApi?.focus(story)
    },
    [story, changeFilter, slug, treeApi],
  )

  const rootClass = clsx(classes.root, {
    [classes.active]: isActive,
  })

  const actionsContainerClass = clsx(classes.actionsContainer, {
    [classes.active]: isActive,
  })

  return (
    <div className={rootClass}>
      <p className={classes.title}>{title}</p>

      {isLeaf ? null : (
        <div className={actionsContainerClass} data-id="tree-actions">
          {isActive && (
            <button className={classes.btn} onPointerDown={onClickUp} type="button">
              <ArrowUpward fontSize="small" />
            </button>
          )}

          <button className={classes.btn} onPointerDown={onClickFilter} type="button">
            {filterIcon}
          </button>
        </div>
      )}
    </div>
  )
}
